import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { apiPost } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'
import { isPlatformAdmin, isSuperAdmin, platformNav, roleLabel, workspaceNav, type NavGroup } from '../lib/roles.ts'
import { useClearSession, useIdleSignOut, useRefreshSession, useSession } from '../lib/session.ts'

/*
 * The mark is the PNG; the word "Blocwork" beside it is text.
 *
 * Not an oversight. The banner render's wordmark is a dark outline over a dark
 * glow — stroke and glow measure within a few points of each other — so it is
 * illegible at header size and cannot be keyed to transparency. The mark has
 * solid fills and crops cleanly, so it carries the brand and live text carries
 * the name, which also keeps the spelling correct and the header selectable and
 * screen-reader friendly. Swap in a single image once a flat vector export
 * exists. Assets are derived by ops/make-brand-assets.py.
 */

/*
 * Two areas (docs/decisions.md 2026-09-30, from the mockup the owner approved):
 *
 *   Platform   /platform/* — customers, campaigns, people, voter data. SuperAdmin
 *              and Org Admin only.
 *   Workspace  everything else — one campaign at a time, chosen in the header,
 *              menu grouped by program and showing only what the campaign bought
 *              and the role allows.
 *
 * "My campaigns" (/campaigns) sits between them for anyone holding more than
 * one campaign: a board of cards, then into one.
 */

export function AppShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()
  const { data: session } = useSession()
  const refresh = useRefreshSession()
  const clear = useClearSession()
  const { campaigns, campaignId, campaign, select } = useSelectedCampaign()

  async function signOut() {
    await apiPost('/auth/logout')
    // Drop every cached query, not just the session: voter counts must not
    // survive a logout in memory for the next person at the same machine.
    clear()
    await refresh()
  }

  useIdleSignOut(session?.state === 'authenticated')

  const email = session?.state === 'authenticated' ? session.email : null
  const mfaBypassed = session?.state === 'authenticated' && session.mfa_bypassed === true
  const admin = isPlatformAdmin(session)

  const inPlatform = pathname.startsWith('/platform')
  const onBoard = pathname === '/campaigns'
  const inWorkspace = !inPlatform && !onBoard && pathname !== '/security'
  const several = campaigns.length > 1

  // The admin's own campaigns: the ones they are on the team of (2026-10-01 —
  // the owner works in a campaign by adding himself as Consultant). Session
  // grants say which; the campaign list says which of those can be opened.
  const mine =
    admin && session?.state === 'authenticated'
      ? campaigns.filter((c) =>
          session.grants.some((g) => g.scope_type === 'campaign' && g.scope_id === c.campaign_id),
        )
      : []

  const groups: NavGroup[] = inPlatform
    ? platformNav(isSuperAdmin(session))
    : onBoard || pathname === '/security'
      ? []
      : workspaceNav(campaign)

  return (
    <div className="min-h-screen bg-surface text-ink">
      {/* DEV SCAFFOLDING — delete with the bypass. Deliberately at the very top,
          full width and unmissable: §6c makes 2FA mandatory with no grace
          period, and an exception nobody can see while using the application is
          one that survives into production. */}
      {mfaBypassed ? (
        <div
          role="status"
          className="bg-gold-500 px-6 py-1.5 text-center text-sm font-semibold text-brand-950"
        >
          Signed in without a second factor — dev bypass is active on this account.
        </div>
      ) : null}
      <header className="bg-header text-header-ink">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/blocwork-mark.png"
              alt=""
              aria-hidden
              width={32}
              height={32}
              className="h-8 w-8 rounded-lg"
            />
            <span className="text-lg font-semibold tracking-tight">Blocwork</span>
          </Link>
          <span className="rounded bg-white/10 px-2 py-0.5 text-xs font-medium">dev</span>

          {/* Where you are. The campaign picker only appears inside a
              campaign's workspace, and only when there is a choice to make —
              a Manager with one campaign simply lives there. */}
          <div className="flex min-w-0 items-center gap-2 text-sm">
            {inPlatform ? <span className="opacity-80">Platform</span> : null}
            {onBoard ? <span className="opacity-80">My campaigns</span> : null}
            {inWorkspace && campaigns.length > 0 ? (
              several ? (
                <label className="flex items-center gap-2">
                  <span className="sr-only">Campaign</span>
                  <select
                    value={campaignId}
                    onChange={(e) => select(e.target.value)}
                    className="max-w-[18rem] rounded-md border border-white/20 bg-brand-900 px-2 py-1 text-sm text-white"
                  >
                    {campaigns.map((c) => (
                      <option key={c.campaign_id} value={c.campaign_id}>
                        {c.name}
                        {admin ? '' : ` · ${roleLabel(c.role)}`}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <span className="truncate opacity-90">{campaign?.name}</span>
              )
            ) : null}
          </div>

          <div className="ml-auto flex items-center gap-3">
            {email ? (
              <Link to="/security" className="hidden text-sm opacity-80 hover:opacity-100 hover:underline sm:inline">
                {email}
              </Link>
            ) : null}
            <button
              type="button"
              onClick={signOut}
              className="rounded-md border border-white/20 px-2.5 py-1 text-xs font-medium hover:bg-white/10"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Phone width: the menu is a scrolling row above the page, not a column
          beside it — a manager unlocking a volunteer's phone does it from their
          own. From md up it is the usual sidebar. */}
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:px-6 md:flex-row md:gap-8 md:py-8">
        {groups.length > 0 || inWorkspace ? (
          <nav
            className="-mx-4 flex gap-4 overflow-x-auto px-4 md:mx-0 md:block md:w-48 md:shrink-0 md:space-y-5 md:overflow-visible md:px-0"
            aria-label="Primary"
          >
            {inWorkspace ? (
              <div className="hidden space-y-1 px-3 md:block">
                {admin ? (
                  <button
                    type="button"
                    onClick={() => void navigate({ to: '/platform/today' })}
                    className="text-xs font-medium text-brand-700 hover:underline"
                  >
                    ‹ Back to Today
                  </button>
                ) : several ? (
                  <Link to="/campaigns" className="text-xs font-medium text-brand-700 hover:underline">
                    ‹ All my campaigns
                  </Link>
                ) : null}
                {campaign ? (
                  <div>
                    <div className="text-sm leading-snug font-semibold">{campaign.name}</div>
                    <div className="text-xs text-ink-muted">{roleLabel(campaign.role)}</div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {groups.map((group, i) => (
              <div key={group.heading ?? `g${i}`} className="contents">
              <div className="shrink-0">
                {group.heading ? (
                  <p className="hidden px-3 pb-1 text-xs font-semibold tracking-wide text-ink-muted uppercase md:block">
                    {group.heading}
                  </p>
                ) : null}
                <ul className="flex gap-1 md:block md:space-y-0.5">
                  {group.items.map((item) => {
                    const active =
                      pathname === item.to || (item.to !== '/' && pathname.startsWith(`${item.to}/`))
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          aria-current={active ? 'page' : undefined}
                          className={
                            active
                              ? 'block whitespace-nowrap rounded-md bg-surface-sunken px-3 py-1.5 text-sm font-semibold text-ink'
                              : 'block whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink'
                          }
                        >
                          {item.label}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
              {inPlatform && i === 0 ? (
                <div className="shrink-0">
                  <p className="hidden px-3 pb-1 text-xs font-semibold tracking-wide text-ink-muted uppercase md:block">
                    My campaigns
                  </p>
                  {mine.length > 0 ? (
                    <ul className="flex gap-1 md:block md:space-y-0.5">
                      {mine.map((c) => (
                        <li key={c.campaign_id}>
                          <button
                            type="button"
                            onClick={() => {
                              select(c.campaign_id)
                              void navigate({ to: '/overview' })
                            }}
                            className="block w-full whitespace-nowrap rounded-md px-3 py-1.5 text-left text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink md:whitespace-normal"
                          >
                            {c.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="hidden px-3 text-xs text-ink-muted md:block">
                      Add yourself to a campaign's team to work in it from here.
                    </p>
                  )}
                </div>
              ) : null}
              </div>
            ))}

          </nav>
        ) : null}

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
